import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    IoAdd, IoTrash, IoSave, IoSettings, IoCheckmarkCircle,
    IoPauseCircle, IoShieldCheckmark, IoPeople, IoBusiness,
    IoPerson, IoGlobe, IoCreate, IoChevronDown, IoChevronForward,
    IoChevronUp, IoClose, IoSearch
} from 'react-icons/io5';
import {
    FaTicketAlt, FaLaptop, FaClipboardList, FaTools, FaExclamationTriangle,
    FaBug, FaFileAlt, FaCog, FaShieldAlt, FaUserCog, FaHeadset, FaDesktop,
    FaServer, FaNetworkWired, FaDatabase, FaChartBar, FaClipboardCheck,
    FaTasks, FaWrench, FaKey, FaEnvelope, FaBell, FaCalendarAlt,
    FaBuilding, FaUsers, FaUserTie, FaHandshake, FaMoneyBillWave,
    FaChartLine, FaLeaf, FaRecycle, FaFire, FaLock, FaGlobe,
    FaPhone, FaCamera, FaPlug, FaWifi, FaPrint, FaHdd, FaTruck,
    FaBoxOpen, FaLightbulb, FaHammer, FaPaintBrush, FaHome
} from 'react-icons/fa';
import AllBoardsManagementPanel from './AllBoardsManagementPanel';
import * as Sentry from '@sentry/react';

/**
 * Curated set of icons for the visual icon picker.
 * Each entry maps a human-friendly label to the FA class name stored in the DB
 * and the React component used for the preview.
 */
const ICON_OPTIONS = [
    { value: 'fa-ticket-alt', label: 'Ticket', Icon: FaTicketAlt },
    { value: 'fa-laptop', label: 'Laptop', Icon: FaLaptop },
    { value: 'fa-clipboard-list', label: 'Checklist', Icon: FaClipboardList },
    { value: 'fa-tools', label: 'Tools', Icon: FaTools },
    { value: 'fa-exclamation-triangle', label: 'Warning', Icon: FaExclamationTriangle },
    { value: 'fa-bug', label: 'Bug', Icon: FaBug },
    { value: 'fa-file-alt', label: 'Document', Icon: FaFileAlt },
    { value: 'fa-cog', label: 'Settings', Icon: FaCog },
    { value: 'fa-shield-alt', label: 'Security', Icon: FaShieldAlt },
    { value: 'fa-user-cog', label: 'User Config', Icon: FaUserCog },
    { value: 'fa-headset', label: 'Support', Icon: FaHeadset },
    { value: 'fa-desktop', label: 'Desktop', Icon: FaDesktop },
    { value: 'fa-server', label: 'Server', Icon: FaServer },
    { value: 'fa-network-wired', label: 'Network', Icon: FaNetworkWired },
    { value: 'fa-database', label: 'Database', Icon: FaDatabase },
    { value: 'fa-chart-bar', label: 'Report', Icon: FaChartBar },
    { value: 'fa-clipboard-check', label: 'Audit', Icon: FaClipboardCheck },
    { value: 'fa-tasks', label: 'Tasks', Icon: FaTasks },
    { value: 'fa-wrench', label: 'Maintenance', Icon: FaWrench },
    { value: 'fa-key', label: 'Access', Icon: FaKey },
    { value: 'fa-envelope', label: 'Email', Icon: FaEnvelope },
    { value: 'fa-bell', label: 'Alert', Icon: FaBell },
    { value: 'fa-calendar-alt', label: 'Calendar', Icon: FaCalendarAlt },
    { value: 'fa-building', label: 'Building', Icon: FaBuilding },
    { value: 'fa-users', label: 'Team', Icon: FaUsers },
    { value: 'fa-user-tie', label: 'Manager', Icon: FaUserTie },
    { value: 'fa-handshake', label: 'Agreement', Icon: FaHandshake },
    { value: 'fa-money-bill-wave', label: 'Finance', Icon: FaMoneyBillWave },
    { value: 'fa-chart-line', label: 'Analytics', Icon: FaChartLine },
    { value: 'fa-leaf', label: 'Sustainability', Icon: FaLeaf },
    { value: 'fa-recycle', label: 'Recycle', Icon: FaRecycle },
    { value: 'fa-fire', label: 'Urgent', Icon: FaFire },
    { value: 'fa-lock', label: 'Lock', Icon: FaLock },
    { value: 'fa-globe', label: 'Global', Icon: FaGlobe },
    { value: 'fa-phone', label: 'Phone', Icon: FaPhone },
    { value: 'fa-camera', label: 'Camera', Icon: FaCamera },
    { value: 'fa-plug', label: 'Plug', Icon: FaPlug },
    { value: 'fa-wifi', label: 'WiFi', Icon: FaWifi },
    { value: 'fa-print', label: 'Printer', Icon: FaPrint },
    { value: 'fa-hdd', label: 'Storage', Icon: FaHdd },
    { value: 'fa-truck', label: 'Delivery', Icon: FaTruck },
    { value: 'fa-box-open', label: 'Package', Icon: FaBoxOpen },
    { value: 'fa-lightbulb', label: 'Idea', Icon: FaLightbulb },
    { value: 'fa-hammer', label: 'Hammer', Icon: FaHammer },
    { value: 'fa-paint-brush', label: 'Design', Icon: FaPaintBrush },
    { value: 'fa-home', label: 'Home', Icon: FaHome },
];

/**
 * BoardCreationRulesPage — Admin settings page for configuring which action item
 * types can be created from the Action Items board, and by which users/roles/departments.
 *
 * When no rules exist, all users see only the Ad-Hoc type (backward-compatible default).
 * Once rules are created, the type selector on the board shows only the types
 * each user is permitted to create based on their role, department, or explicit grants.
 */
const BoardCreationRulesPage = ({ onNavigateToTab }) => {
    const { apiFetch, user } = useAuth();
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editRule, setEditRule] = useState(null);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Icon picker search filter
    const [iconSearch, setIconSearch] = useState('');

    // Lookup data
    const [availableContexts, setAvailableContexts] = useState([]);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [allTemplates, setAllTemplates] = useState([]);
    const [changingTemplate, setChangingTemplate] = useState(false);

    // Accordion state for grouped contexts
    const [expandedModules, setExpandedModules] = useState({});

    // Searchable multi-select state for access scope
    const [roleSearch, setRoleSearch] = useState('');
    const [deptSearch, setDeptSearch] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const [showDeptDropdown, setShowDeptDropdown] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const roleSearchRef = useRef(null);
    const deptSearchRef = useRef(null);
    const userSearchRef = useRef(null);

    const fetchRules = useCallback(async () => {
        try {
            const res = await apiFetch('/api/BoardCreationRules?includeInactive=true');
            const data = await res.json();
            if (data.success) setRules(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to load board creation rules');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    const fetchContexts = useCallback(async () => {
        try {
            const res = await apiFetch('/api/BoardCreationRules/available-contexts');
            const data = await res.json();
            if (data.success) setAvailableContexts(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
        }
    }, [apiFetch]);

    const fetchRoles = useCallback(async () => {
        try {
            const res = await apiFetch('/api/User/roles');
            const data = await res.json();
            if (data.success) setRoles(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
        }
    }, [apiFetch]);

    const fetchDepartments = useCallback(async () => {
        try {
            const res = await apiFetch('/api/Department');
            const data = await res.json();
            if (data.success) setDepartments(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
        }
    }, [apiFetch]);

    const fetchUsers = useCallback(async () => {
        try {
            const res = await apiFetch('/api/User/all');
            const data = await res.json();
            if (data.success) setUsers(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
        }
    }, [apiFetch]);

    const fetchTemplates = useCallback(async () => {
        try {
            const res = await apiFetch('/api/Templates');
            const data = await res.json();
            if (data.success) setAllTemplates(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchRules();
        fetchContexts();
        fetchRoles();
        fetchDepartments();
        fetchUsers();
        fetchTemplates();
    }, [fetchRules, fetchContexts, fetchRoles, fetchDepartments, fetchUsers, fetchTemplates]);

    /** Close modals with Escape key */
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (deleteTarget) setDeleteTarget(null);
                else if (editRule) handleClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [editRule, deleteTarget]);

    /** Close access scope dropdowns when clicking outside */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (roleSearchRef.current && !roleSearchRef.current.contains(event.target)) setShowRoleDropdown(false);
            if (deptSearchRef.current && !deptSearchRef.current.contains(event.target)) setShowDeptDropdown(false);
            if (userSearchRef.current && !userSearchRef.current.contains(event.target)) setShowUserDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /** Group available contexts by module for the selection dropdown */
    const contextsByModule = availableContexts.reduce((acc, ctx) => {
        const key = ctx.moduleDisplayName || ctx.moduleKey;
        if (!acc[key]) acc[key] = [];
        acc[key].push(ctx);
        return acc;
    }, {});

    /** IDs already in rules — prevent duplicates */
    const usedAssignmentIds = new Set(rules.map(r => r.moduleTemplateAssignmentId));

    const handleCreate = () => {
        setEditRule({
            moduleTemplateAssignmentId: '',
            displayName: '',
            description: '',
            icon: '',
            allowedRoleIds: '',
            allowedDepartmentIds: '',
            allowedUserIds: '',
            allowAll: false,
            sortOrder: rules.length,
            isActive: true,
            configJson: ''
        });
        setIsCreateMode(true);
    };

    const handleEdit = (rule) => {
        setEditRule({ ...rule });
        setIsCreateMode(false);
    };

    const handleClose = () => {
        setEditRule(null);
        setIsCreateMode(false);
        setIconSearch('');
        setRoleSearch('');
        setDeptSearch('');
        setUserSearch('');
        setShowRoleDropdown(false);
        setShowDeptDropdown(false);
        setShowUserDropdown(false);
    };

    const handleFieldChange = (field, value) => {
        setEditRule(prev => ({ ...prev, [field]: value }));
    };

    /** Multi-select chip toggle helper */
    const toggleChip = (field, id) => {
        setEditRule(prev => {
            const current = (prev[field] || '').split(',').map(s => s.trim()).filter(Boolean);
            const strId = String(id);
            const updated = current.includes(strId)
                ? current.filter(i => i !== strId)
                : [...current, strId];
            return { ...prev, [field]: updated.join(',') };
        });
    };

    /** Move a rule up or down in the list and persist to backend */
    const handleReorder = async (ruleId, direction) => {
        const idx = rules.findIndex(r => r.id === ruleId);
        if (idx < 0) return;
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= rules.length) return;

        // Optimistic swap
        const newRules = [...rules];
        [newRules[idx], newRules[swapIdx]] = [newRules[swapIdx], newRules[idx]];
        setRules(newRules);

        // Persist
        try {
            const payload = newRules.map((r, i) => ({ id: r.id, sortOrder: i }));
            await apiFetch('/api/BoardCreationRules/reorder', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to reorder');
            fetchRules();
        }
    };

    const handleSave = async () => {
        if (!editRule.moduleTemplateAssignmentId && isCreateMode) {
            toast.error('Please select an action item type');
            return;
        }

        setSaving(true);
        try {
            const method = isCreateMode ? 'POST' : 'PUT';
            const url = isCreateMode
                ? '/api/BoardCreationRules'
                : `/api/BoardCreationRules/${editRule.id}`;

            const payload = {
                ...editRule,
                moduleTemplateAssignmentId: parseInt(editRule.moduleTemplateAssignmentId) || editRule.moduleTemplateAssignmentId,
                userId: user?.userID
            };

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                toast.success(isCreateMode ? 'Rule created' : 'Rule updated');
                handleClose();
                fetchRules();
            } else {
                toast.error(data?.message || 'Failed to save');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error saving rule');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (rule) => {
        try {
            const res = await apiFetch(`/api/BoardCreationRules/${rule.id}/toggle`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                fetchRules();
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to toggle rule');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await apiFetch(`/api/BoardCreationRules/${deleteTarget.id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success('Rule deleted');
                fetchRules();
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to delete');
        } finally {
            setDeleteTarget(null);
        }
    };

    /** Change the template assigned to a rule's ModuleTemplateAssignment */
    const handleTemplateChange = async (newTemplateKey) => {
        if (!editRule?.moduleTemplateAssignmentId) return;
        setChangingTemplate(true);
        try {
            const res = await apiFetch(`/api/ModuleTemplateAssignments/${editRule.moduleTemplateAssignmentId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assignedTemplateKey: newTemplateKey || null, userId: user.userID })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setEditRule(prev => ({ ...prev, assignedTemplateKey: newTemplateKey || null }));
                toast.success('Template updated');
                fetchContexts();
            } else {
                toast.error(data?.message || 'Failed to update template');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error updating template');
        } finally {
            setChangingTemplate(false);
        }
    };

    /** Resolve display values for access scope badges */
    const getRoleName = (roleId) => {
        const r = roles.find(r => r.roleID === parseInt(roleId));
        return r?.roleName || `Role #${roleId}`;
    };
    const getDeptName = (deptId) => {
        const d = departments.find(d => d.departmentID === parseInt(deptId));
        return d?.departmentName || `Dept #${deptId}`;
    };
    const getUserName = (userId) => {
        const u = users.find(u => u.id === parseInt(userId));
        return u?.name || u?.email || `User #${userId}`;
    };

    // ── Styles ──────────────────────────────────────────────────────────────

    const styles = {
        page: { padding: '0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
        title: { margin: 0, fontSize: '1.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' },
        subtitle: { color: '#6b7280', fontSize: '0.9rem', marginTop: '4px' },
        btnCreate: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 18px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, fontSize: '0.9rem' },
        empty: { textAlign: 'center', padding: '48px 24px', background: '#f9fafb', borderRadius: '8px', color: '#6b7280' },
        tableWrapper: { border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' },
        table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
        th: { textAlign: 'left', padding: '12px 16px', background: '#57565B', borderBottom: '3px solid #F63040', fontWeight: 600, color: '#fff', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
        td: { padding: '12px 16px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
        badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 500 },
        badgeActive: { background: '#d1fae5', color: '#065f46' },
        badgeInactive: { background: '#fee2e2', color: '#991b1b' },
        scopeBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 500, marginRight: '4px', marginBottom: '2px' },
        scopeRole: { background: '#dbeafe', color: '#1e40af' },
        scopeDept: { background: '#fef3c7', color: '#92400e' },
        scopeUser: { background: '#ede9fe', color: '#5b21b6' },
        scopeAll: { background: '#d1fae5', color: '#065f46' },
        btnEdit: { padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#374151', fontWeight: 500, transition: 'all 0.15s' },
        btnToggle: { padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#374151', fontWeight: 500, transition: 'all 0.15s' },
        btnDanger: { padding: '6px 12px', border: '1px solid #fca5a5', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 500, transition: 'all 0.15s' },
        btnReorder: { padding: '4px', background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', transition: 'all 0.15s', lineHeight: 1 },
        btnReorderDisabled: { opacity: 0.3, cursor: 'default', pointerEvents: 'none' },
        actions: { display: 'flex', gap: '8px', alignItems: 'center' },
        // Modal
        overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        modal: { background: '#fff', borderRadius: '12px', width: '680px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
        modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e5e7eb' },
        modalBody: { padding: '24px' },
        modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', borderTop: '1px solid #e5e7eb' },
        formField: { marginBottom: '16px' },
        label: { display: 'block', fontWeight: 500, marginBottom: '6px', fontSize: '0.9rem', color: '#374151' },
        input: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' },
        select: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' },
        helpText: { display: 'block', fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' },
        sectionTitle: { fontSize: '1rem', fontWeight: 600, margin: '20px 0 12px', padding: '8px 0', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '8px', color: '#374151' },
        checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' },
        moduleGroup: { cursor: 'pointer', padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, fontSize: '0.9rem' },
        contextOption: { padding: '8px 16px 8px 28px', cursor: 'pointer', borderRadius: '4px', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        contextOptionHover: { background: '#f3f4f6' },
        contextAlreadyUsed: { opacity: 0.4, pointerEvents: 'none' },
        btnCancel: { padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.9rem', color: '#374151', fontWeight: 500 },
        btnSave: { padding: '8px 18px', border: 'none', borderRadius: '6px', background: '#dc3545', color: '#fff', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' },
        infoBox: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '14px 18px', marginBottom: '20px', fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.6 },
        typeName: { fontWeight: 600, fontSize: '0.95rem' },
        typeModule: { fontSize: '0.8rem', color: '#6b7280' },
        setupGuide: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' },
        setupGuideHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' },
        setupSteps: { display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' },
        setupStep: { display: 'flex', alignItems: 'flex-start', gap: '10px', flex: '1', minWidth: '160px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 14px' },
        stepNum: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', background: '#dc3545', color: '#fff', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0, marginTop: '2px' },
        stepDesc: { display: 'block', fontSize: '0.78rem', color: '#6b7280', marginTop: '2px', lineHeight: 1.4 },
        stepArrow: { fontSize: '1.2rem', color: '#9ca3af', alignSelf: 'center', flexShrink: 0 },
        btnLink: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, padding: '4px 8px', borderRadius: '4px', textDecoration: 'underline' },
        // Icon picker
        iconGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: '6px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', marginTop: '6px' },
        iconCell: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', borderRadius: '8px', cursor: 'pointer', border: '2px solid transparent', transition: 'all 0.15s', gap: '4px', minHeight: '56px' },
        iconCellSelected: { border: '2px solid #2563eb', background: '#eff6ff' },
        iconCellHover: { background: '#f3f4f6' },
        iconLabel: { fontSize: '0.65rem', color: '#6b7280', textAlign: 'center', lineHeight: 1.2 },
        iconSearchWrapper: { position: 'relative', marginTop: '6px' },
        iconSearchInput: { width: '100%', padding: '6px 10px 6px 30px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' },
        iconSearchIcon: { position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' },
        selectedIconPreview: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '0.85rem', color: '#1e40af', marginBottom: '8px' },
        clearIconBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px', display: 'inline-flex', alignItems: 'center' },
        // Searchable multi-select
        searchableSelect: { position: 'relative', marginTop: '6px' },
        searchInput: { width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' },
        searchInputIcon: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' },
        dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '2px', maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
        dropdownItem: { padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.1s' },
        dropdownEmpty: { padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' },
        selectedTags: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
        tag: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500 },
        tagRole: { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
        tagDept: { background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24' },
        tagUser: { background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd' },
        tagRemove: { background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', display: 'inline-flex', alignItems: 'center', color: 'inherit', opacity: 0.7 },
    };

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        <IoShieldCheckmark size={24} /> Board Creation Rules
                    </h1>
                    <p style={styles.subtitle}>
                        Control which action item types users can create from the Action Items board
                    </p>
                </div>
                <button style={styles.btnCreate} onClick={handleCreate}>
                    <IoAdd size={18} /> Add Rule
                </button>
            </div>

            <div style={styles.setupGuide}>
                <div style={styles.setupGuideHeader}>
                    <strong>⚙️ Setup Guide — How action item types work</strong>
                    {onNavigateToTab && (
                        <button style={styles.btnLink} onClick={() => onNavigateToTab('moduleTemplateConfig')}>
                            → Go to Module Assignments
                        </button>
                    )}
                </div>
                <div style={styles.setupSteps}>
                    <div style={{ ...styles.setupStep, ...(onNavigateToTab ? { cursor: 'pointer' } : {}) }} onClick={() => onNavigateToTab && onNavigateToTab('templateManagement')} onKeyDown={e => { if (e.key === 'Enter' && onNavigateToTab) onNavigateToTab('templateManagement'); }} role={onNavigateToTab ? 'button' : undefined} tabIndex={onNavigateToTab ? 0 : undefined} title={onNavigateToTab ? 'Go to Item Templates' : undefined}>
                        <span style={styles.stepNum}>1</span>
                        <div>
                            <strong>Item Templates</strong>
                            <span style={styles.stepDesc}>Design the form fields for each action item type (e.g. IT Support Request form).</span>
                        </div>
                    </div>
                    <span style={styles.stepArrow}>→</span>
                    <div style={{ ...styles.setupStep, ...(onNavigateToTab ? { cursor: 'pointer' } : {}) }} onClick={() => onNavigateToTab && onNavigateToTab('moduleTemplateConfig')} onKeyDown={e => { if (e.key === 'Enter' && onNavigateToTab) onNavigateToTab('moduleTemplateConfig'); }} role={onNavigateToTab ? 'button' : undefined} tabIndex={onNavigateToTab ? 0 : undefined} title={onNavigateToTab ? 'Go to Module Assignments' : undefined}>
                        <span style={styles.stepNum}>2</span>
                        <div>
                            <strong>Module Assignments</strong>
                            <span style={styles.stepDesc}>Link your templates to a context (e.g. IT / Support Ticket uses &quot;IT Support Template&quot;).</span>
                        </div>
                    </div>
                    <span style={styles.stepArrow}>→</span>
                    <div style={styles.setupStep}>
                        <span style={styles.stepNum}>3</span>
                        <div>
                            <strong>Board Creation Rules</strong> <span style={{ color: '#dc3545', fontSize: '0.75rem' }}>← You are here</span>
                            <span style={styles.stepDesc}>Choose which contexts appear in the &quot;Create Action Item&quot; type selector, and who can use them.</span>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '32px' }}>Loading...</div>
            ) : rules.length === 0 ? (
                <div style={styles.empty}>
                    <IoSettings size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <h3 style={{ margin: '0 0 8px' }}>No rules configured yet</h3>
                    <p style={{ maxWidth: '460px', margin: '0 auto 16px' }}>
                        All users currently see only the default <strong>Ad-Hoc</strong> type when creating an action item.
                        Add rules to expose additional types (e.g. IT Support Ticket, Procurement Pack) and control who can use them.
                    </p>
                    {onNavigateToTab && (
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '16px' }}>
                            First make sure your contexts have templates assigned in{' '}
                            <button
                                style={{ ...styles.btnLink, display: 'inline', padding: 0, fontSize: '0.85rem' }}
                                onClick={() => onNavigateToTab('moduleTemplateConfig')}
                            >
                                Module Assignments
                            </button>
                            , then come back here to create access rules.
                        </p>
                    )}
                    <button style={{ ...styles.btnCreate, display: 'inline-flex', marginTop: '4px' }} onClick={handleCreate}>
                        <IoAdd size={18} /> Add First Rule
                    </button>
                </div>
            ) : (
                <div style={styles.tableWrapper}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}></th>
                            <th style={styles.th}>Type</th>
                            <th style={styles.th}>Access Scope</th>
                            <th style={styles.th}>Template</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule, idx) => (
                            <tr key={rule.id} style={{ transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                                <td style={{ ...styles.td, textAlign: 'center', width: '40px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                        <button
                                            style={{ ...styles.btnReorder, ...(idx === 0 ? styles.btnReorderDisabled : {}) }}
                                            onClick={() => handleReorder(rule.id, 'up')}
                                            title="Move up"
                                            disabled={idx === 0}
                                        >
                                            <IoChevronUp size={14} />
                                        </button>
                                        <button
                                            style={{ ...styles.btnReorder, ...(idx === rules.length - 1 ? styles.btnReorderDisabled : {}) }}
                                            onClick={() => handleReorder(rule.id, 'down')}
                                            title="Move down"
                                            disabled={idx === rules.length - 1}
                                        >
                                            <IoChevronDown size={14} />
                                        </button>
                                    </div>
                                </td>
                                <td style={styles.td}>
                                    <div style={styles.typeName}>
                                        {rule.displayName || rule.contextDisplayName || rule.contextKey}
                                    </div>
                                    <div style={styles.typeModule}>
                                        {rule.moduleDisplayName} / {rule.contextKey}
                                    </div>
                                </td>
                                <td style={styles.td}>
                                    {rule.allowAll ? (
                                        <span style={{ ...styles.scopeBadge, ...styles.scopeAll }}>
                                            <IoGlobe size={12} /> Everyone
                                        </span>
                                    ) : (
                                        <>
                                            {rule.allowedRoleIds && rule.allowedRoleIds.split(',').filter(Boolean).map(id => (
                                                <span key={`r${id}`} style={{ ...styles.scopeBadge, ...styles.scopeRole }}>
                                                    <IoPeople size={10} /> {getRoleName(id)}
                                                </span>
                                            ))}
                                            {rule.allowedDepartmentIds && rule.allowedDepartmentIds.split(',').filter(Boolean).map(id => (
                                                <span key={`d${id}`} style={{ ...styles.scopeBadge, ...styles.scopeDept }}>
                                                    <IoBusiness size={10} /> {getDeptName(id)}
                                                </span>
                                            ))}
                                            {rule.allowedUserIds && rule.allowedUserIds.split(',').filter(Boolean).map(id => (
                                                <span key={`u${id}`} style={{ ...styles.scopeBadge, ...styles.scopeUser }}>
                                                    <IoPerson size={10} /> {getUserName(id)}
                                                </span>
                                            ))}
                                            {!rule.allowedRoleIds && !rule.allowedDepartmentIds && !rule.allowedUserIds && (
                                                <span style={{ ...styles.scopeBadge, ...styles.scopeAll }}>
                                                    <IoGlobe size={12} /> Unrestricted
                                                </span>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td style={styles.td}>
                                    <span style={{ fontSize: '0.85rem', color: rule.assignedTemplateKey ? '#374151' : '#9ca3af' }}>
                                        {rule.assignedTemplateKey || 'No template'}
                                    </span>
                                </td>
                                <td style={styles.td}>
                                    <span style={{ ...styles.badge, ...(rule.isActive ? styles.badgeActive : styles.badgeInactive) }}>
                                        {rule.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td style={styles.td}>
                                    <div style={styles.actions}>
                                        <button style={styles.btnEdit} onClick={() => handleEdit(rule)} title="Edit rule">
                                            <IoCreate size={14} /> Edit
                                        </button>
                                        <button style={styles.btnToggle} onClick={() => handleToggle(rule)} title={rule.isActive ? 'Deactivate' : 'Activate'}>
                                            {rule.isActive ? <IoPauseCircle size={14} /> : <IoCheckmarkCircle size={14} />}
                                            {rule.isActive ? 'Pause' : 'Enable'}
                                        </button>
                                        <button style={styles.btnDanger} onClick={() => setDeleteTarget(rule)} title="Delete rule">
                                            <IoTrash size={14} /> Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            {/* Edit / Create Modal */}
            {editRule && (
                <div style={styles.overlay} onClick={handleClose}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
                                {isCreateMode ? 'Add Board Creation Rule' : 'Edit Rule'}
                            </h2>
                            <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
                        </div>
                        <div style={styles.modalBody}>
                            {/* Action Item Type Selection (create only) / Read-only info (edit) */}
                            {isCreateMode ? (
                                <div style={styles.formField}>
                                    <label style={styles.label}>Action Item Type *</label>
                                    <small style={styles.helpText}>Select which action item type this rule controls access to</small>
                                    <div style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '8px' }}>
                                        {Object.entries(contextsByModule).map(([moduleName, contexts]) => (
                                            <div key={moduleName}>
                                                <div
                                                    style={styles.moduleGroup}
                                                    onClick={() => setExpandedModules(prev => ({ ...prev, [moduleName]: !prev[moduleName] }))}
                                                >
                                                    {expandedModules[moduleName] ? <IoChevronDown size={14} /> : <IoChevronForward size={14} />}
                                                    {moduleName}
                                                    <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#9ca3af' }}>
                                                        {contexts.length} types
                                                    </span>
                                                </div>
                                                {expandedModules[moduleName] && contexts.map(ctx => {
                                                    const alreadyUsed = usedAssignmentIds.has(ctx.id);
                                                    const isSelected = editRule.moduleTemplateAssignmentId === ctx.id;
                                                    return (
                                                        <div
                                                            key={ctx.id}
                                                            style={{
                                                                ...styles.contextOption,
                                                                ...(alreadyUsed ? styles.contextAlreadyUsed : {}),
                                                                ...(isSelected ? { background: '#eff6ff', fontWeight: 600 } : {})
                                                            }}
                                                            onClick={() => !alreadyUsed && handleFieldChange('moduleTemplateAssignmentId', ctx.id)}
                                                        >
                                                            <span>
                                                                {ctx.contextDisplayName}
                                                                {alreadyUsed && <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#9ca3af' }}>(already configured)</span>}
                                                            </span>
                                                            {isSelected && <IoCheckmarkCircle size={16} color="#2563eb" />}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div style={styles.infoBox}>
                                    <strong>{editRule.displayName || editRule.contextDisplayName || editRule.contextKey}</strong>
                                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>
                                        {editRule.moduleDisplayName} / {editRule.contextKey}
                                    </div>
                                </div>
                            )}

                            {/* Template assignment — only shown for existing rules */}
                            {!isCreateMode && (
                                <div style={styles.formField}>
                                    <label style={styles.label}>Assigned Template</label>
                                    <select
                                        style={styles.select}
                                        value={editRule.assignedTemplateKey || ''}
                                        onChange={e => handleTemplateChange(e.target.value)}
                                        disabled={changingTemplate}
                                    >
                                        <option value="">— None (manual fallback) —</option>
                                        {allTemplates.map(t => (
                                            <option key={t.templateKey} value={t.templateKey}>
                                                {t.displayName || t.templateKey}
                                            </option>
                                        ))}
                                    </select>
                                    <small style={styles.helpText}>
                                        Template used when creating this type of action item.{' '}
                                        {editRule.assignedTemplateKey && (
                                            <button
                                                type="button"
                                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontWeight: 500, textDecoration: 'underline' }}
                                                onClick={() => onNavigateToTab('templateManagement')}
                                            >
                                                Edit template →
                                            </button>
                                        )}
                                    </small>
                                </div>
                            )}

                            {/* Display name override */}
                            <div style={styles.formField}>
                                <label style={styles.label}>Display Name Override</label>
                                <input
                                    style={styles.input}
                                    value={editRule.displayName || ''}
                                    onChange={e => handleFieldChange('displayName', e.target.value)}
                                    placeholder="Leave blank to use the default context name"
                                />
                                <small style={styles.helpText}>Custom name shown in the type selector on the board</small>
                            </div>

                            <div style={styles.formField}>
                                <label style={styles.label}>Description</label>
                                <input
                                    style={styles.input}
                                    value={editRule.description || ''}
                                    onChange={e => handleFieldChange('description', e.target.value)}
                                    placeholder="Short description for the type selector"
                                />
                            </div>

                            <div style={styles.formField}>
                                <label style={styles.label}>Icon</label>
                                <small style={styles.helpText}>Choose an icon for this type (shown in the selector on the board). Leave blank for the default.</small>
                                {/* Show current selection with clear button */}
                                {editRule.icon && (() => {
                                    const match = ICON_OPTIONS.find(o => o.value === editRule.icon);
                                    return (
                                        <div style={styles.selectedIconPreview}>
                                            {match ? <match.Icon size={16} /> : <span style={{ fontFamily: 'FontAwesome' }}>{editRule.icon}</span>}
                                            <span>{match?.label || editRule.icon}</span>
                                            <button type="button" style={styles.clearIconBtn} onClick={() => handleFieldChange('icon', '')} title="Clear icon">
                                                <IoClose size={14} />
                                            </button>
                                        </div>
                                    );
                                })()}
                                {/* Search filter */}
                                <div style={styles.iconSearchWrapper}>
                                    <IoSearch size={14} style={styles.iconSearchIcon} />
                                    <input
                                        style={styles.iconSearchInput}
                                        placeholder="Search icons..."
                                        value={iconSearch}
                                        onChange={e => setIconSearch(e.target.value)}
                                    />
                                </div>
                                {/* Icon grid */}
                                <div style={styles.iconGrid}>
                                    {ICON_OPTIONS
                                        .filter(opt => !iconSearch || opt.label.toLowerCase().includes(iconSearch.toLowerCase()))
                                        .map(opt => {
                                            const isSelected = editRule.icon === opt.value;
                                            return (
                                                <div
                                                    key={opt.value}
                                                    style={{
                                                        ...styles.iconCell,
                                                        ...(isSelected ? styles.iconCellSelected : {})
                                                    }}
                                                    onClick={() => handleFieldChange('icon', isSelected ? '' : opt.value)}
                                                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f3f4f6'; }}
                                                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ''; }}
                                                    title={opt.label}
                                                >
                                                    <opt.Icon size={20} color={isSelected ? '#2563eb' : '#6b7280'} />
                                                    <span style={styles.iconLabel}>{opt.label}</span>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>

                            {/* Access Scope */}
                            <div style={styles.sectionTitle}>
                                <IoShieldCheckmark size={18} /> Access Scope
                            </div>
                            <small style={styles.helpText}>
                                Control who can see and create this action item type. When &quot;Allow all users&quot; is unchecked, only users matching at least one of the role, department, or specific user grants below will see this type in the creation selector.
                            </small>

                            <div style={styles.checkboxRow}>
                                <input
                                    type="checkbox"
                                    id="allowAll"
                                    checked={editRule.allowAll}
                                    onChange={e => handleFieldChange('allowAll', e.target.checked)}
                                />
                                <label htmlFor="allowAll" style={{ fontWeight: 500, cursor: 'pointer' }}>
                                    <IoGlobe size={14} /> Allow all users (no restrictions)
                                </label>
                            </div>

                            {!editRule.allowAll && (
                                <>
                                    {/* Roles — Searchable multi-select */}
                                    <div style={styles.formField}>
                                        <label style={styles.label}><IoPeople size={14} /> Allowed Roles</label>
                                        <small style={styles.helpText}>Search and select roles. Leave empty for no role restriction.</small>
                                        {/* Selected role tags */}
                                        {editRule.allowedRoleIds && (
                                            <div style={styles.selectedTags}>
                                                {editRule.allowedRoleIds.split(',').filter(Boolean).map(id => (
                                                    <span key={`rt${id}`} style={{ ...styles.tag, ...styles.tagRole }}>
                                                        {getRoleName(id)}
                                                        <button type="button" style={styles.tagRemove} onClick={() => toggleChip('allowedRoleIds', id)} title="Remove">
                                                            <IoClose size={12} />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div style={styles.searchableSelect} ref={roleSearchRef}>
                                            <IoSearch size={14} style={styles.searchInputIcon} />
                                            <input
                                                style={styles.searchInput}
                                                placeholder="Search roles..."
                                                value={roleSearch}
                                                onChange={e => { setRoleSearch(e.target.value); setShowRoleDropdown(true); }}
                                                onFocus={() => setShowRoleDropdown(true)}
                                            />
                                            {showRoleDropdown && (() => {
                                                const selectedIds = (editRule.allowedRoleIds || '').split(',').filter(Boolean);
                                                const filtered = roles.filter(r =>
                                                    !selectedIds.includes(String(r.roleID)) &&
                                                    (!roleSearch || r.roleName.toLowerCase().includes(roleSearch.toLowerCase()))
                                                );
                                                return (
                                                    <div style={styles.dropdown}>
                                                        {filtered.length === 0 ? (
                                                            <div style={styles.dropdownEmpty}>{roleSearch ? 'No matching roles' : 'All roles selected'}</div>
                                                        ) : filtered.map(r => (
                                                            <div
                                                                key={r.roleID}
                                                                style={styles.dropdownItem}
                                                                onClick={() => { toggleChip('allowedRoleIds', r.roleID); setRoleSearch(''); setShowRoleDropdown(false); }}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                                            >
                                                                {r.roleName}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Departments — Searchable multi-select */}
                                    <div style={styles.formField}>
                                        <label style={styles.label}><IoBusiness size={14} /> Allowed Departments</label>
                                        <small style={styles.helpText}>Search and select departments (e.g., only HR can create IT Onboarding)</small>
                                        {editRule.allowedDepartmentIds && (
                                            <div style={styles.selectedTags}>
                                                {editRule.allowedDepartmentIds.split(',').filter(Boolean).map(id => (
                                                    <span key={`dt${id}`} style={{ ...styles.tag, ...styles.tagDept }}>
                                                        {getDeptName(id)}
                                                        <button type="button" style={styles.tagRemove} onClick={() => toggleChip('allowedDepartmentIds', id)} title="Remove">
                                                            <IoClose size={12} />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div style={styles.searchableSelect} ref={deptSearchRef}>
                                            <IoSearch size={14} style={styles.searchInputIcon} />
                                            <input
                                                style={styles.searchInput}
                                                placeholder="Search departments..."
                                                value={deptSearch}
                                                onChange={e => { setDeptSearch(e.target.value); setShowDeptDropdown(true); }}
                                                onFocus={() => setShowDeptDropdown(true)}
                                            />
                                            {showDeptDropdown && (() => {
                                                const selectedIds = (editRule.allowedDepartmentIds || '').split(',').filter(Boolean);
                                                const filtered = departments.filter(d =>
                                                    !selectedIds.includes(String(d.departmentID)) &&
                                                    (!deptSearch || d.departmentName.toLowerCase().includes(deptSearch.toLowerCase()))
                                                );
                                                return (
                                                    <div style={styles.dropdown}>
                                                        {filtered.length === 0 ? (
                                                            <div style={styles.dropdownEmpty}>{deptSearch ? 'No matching departments' : 'All departments selected'}</div>
                                                        ) : filtered.map(d => (
                                                            <div
                                                                key={d.departmentID}
                                                                style={styles.dropdownItem}
                                                                onClick={() => { toggleChip('allowedDepartmentIds', d.departmentID); setDeptSearch(''); setShowDeptDropdown(false); }}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                                            >
                                                                {d.departmentName}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Specific Users — Searchable multi-select */}
                                    <div style={styles.formField}>
                                        <label style={styles.label}><IoPerson size={14} /> Allowed Users</label>
                                        <small style={styles.helpText}>Search and select users to grant specific access (in addition to role/department scope)</small>
                                        {editRule.allowedUserIds && (
                                            <div style={styles.selectedTags}>
                                                {editRule.allowedUserIds.split(',').filter(Boolean).map(id => (
                                                    <span key={`ut${id}`} style={{ ...styles.tag, ...styles.tagUser }}>
                                                        {getUserName(id)}
                                                        <button type="button" style={styles.tagRemove} onClick={() => toggleChip('allowedUserIds', id)} title="Remove">
                                                            <IoClose size={12} />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div style={styles.searchableSelect} ref={userSearchRef}>
                                            <IoSearch size={14} style={styles.searchInputIcon} />
                                            <input
                                                style={styles.searchInput}
                                                placeholder="Search users by name or email..."
                                                value={userSearch}
                                                onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                                                onFocus={() => setShowUserDropdown(true)}
                                            />
                                            {showUserDropdown && (() => {
                                                const selectedIds = (editRule.allowedUserIds || '').split(',').filter(Boolean);
                                                const filtered = users.filter(u => {
                                                    const uid = String(u.id);
                                                    if (selectedIds.includes(uid)) return false;
                                                    if (!userSearch) return true;
                                                    const q = userSearch.toLowerCase();
                                                    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                                                }).slice(0, 20);
                                                return (
                                                    <div style={styles.dropdown}>
                                                        {filtered.length === 0 ? (
                                                            <div style={styles.dropdownEmpty}>{userSearch ? 'No matching users' : 'All users selected'}</div>
                                                        ) : filtered.map(u => {
                                                            return (
                                                                <div
                                                                    key={u.id}
                                                                    style={styles.dropdownItem}
                                                                    onClick={() => { toggleChip('allowedUserIds', u.id); setUserSearch(''); setShowUserDropdown(false); }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = ''}
                                                                >
                                                                    <span>{u.name || u.email}</span>
                                                                    {u.email && <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{u.email}</span>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Active toggle */}
                            <div style={{ ...styles.checkboxRow, marginTop: '16px' }}>
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={editRule.isActive !== false}
                                    onChange={e => handleFieldChange('isActive', e.target.checked)}
                                />
                                <label htmlFor="isActive" style={{ cursor: 'pointer' }}>Active</label>
                            </div>
                        </div>
                        <div style={styles.modalFooter}>
                            <button style={styles.btnCancel} onClick={handleClose}>Cancel</button>
                            <button style={styles.btnSave} onClick={handleSave} disabled={saving}>
                                <IoSave size={16} />
                                {saving ? 'Saving...' : 'Save Rule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {deleteTarget && (
                <div style={styles.overlay} onClick={() => setDeleteTarget(null)}>
                    <div style={{ ...styles.modal, width: '420px' }} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Delete Rule</h2>
                        </div>
                        <div style={styles.modalBody}>
                            <p>Delete the rule for <strong>{deleteTarget.displayName || deleteTarget.contextDisplayName || deleteTarget.contextKey}</strong>?</p>
                            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Users will no longer be able to create this type from the board.</p>
                        </div>
                        <div style={styles.modalFooter}>
                            <button style={styles.btnCancel} onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button style={{ ...styles.btnSave, background: '#dc2626' }} onClick={handleDelete}>
                                <IoTrash size={16} /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* All Boards Management Panel (admin-only, for orphan board transfer) */}
            <AllBoardsManagementPanel />
        </div>
    );
};

export default BoardCreationRulesPage;
